import React, { useState } from 'react';
import {
  Button,
  Dialog,
  HSpacer,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { FileUpload } from '@/components/shared/FileUpload';
import { ImportApi } from '@/utilities/api/ImportApi';
import { ImportType } from '@shared/enums';
import { downloadCsv } from '@/utilities/Export';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { useTranslation } from 'react-i18next';

interface CsvUploadModalProps {
  open: boolean,
  onClose: () => void,
  onUpload: (file: File) => void,
}

export const CsvUploadModal: React.FC<CsvUploadModalProps> = ({ open, onClose, onUpload }) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);

  const { openSnackbar } = useSnackbar();

  const handleFileChange = (file: File | undefined) => {
    if (file) {
      if (file.size > 1024 * 1024) {
        openSnackbar(t($ => $.notificationBuilder.modal.csvUpload.errors.fileTooLarge));
        return;
      }
      if (!file.name.toLowerCase().endsWith('.csv')) {
        openSnackbar(t($ => $.notificationBuilder.modal.csvUpload.errors.invalidFileType));
        return;
      }

      setSelectedFile(file);
    } else {
      setSelectedFile(undefined);
    }
  };

  const handleSave = () => {
    if (selectedFile) {
      onUpload(selectedFile);
    }
    setSelectedFile(undefined);
    onClose();
  };

  return (
    <Dialog
      data-testid="csv-upload-modal"
      fullWidth
      maxWidth="xs"
      onClose={onClose}
      open={open}
      showCloseButton={false}
      sx={{
        '& .MuiDialog-paper': {
          padding: '20px 24px 24px 24px',
        },
      }}
      testID="csv-upload-modal"
    >
      <DialogTitle sx={{ padding: 0 }}>
        <Stack>
          <Text category="headline-small">
            {t($ => $.notificationBuilder.modal.csvUpload.title)}
          </Text>
          <VSpacer size="5" />
          <Text
            category="body-medium"
            sx={{ color: 'text.secondary' }}
          >
            {t($ => $.notificationBuilder.modal.csvUpload.description)}
            <HSpacer size="2" />
            <Button
              onClick={async () => {
                const csv = await ImportApi.getTemplate(ImportType.ScheduledNotifications);
                downloadCsv(csv, `${ImportType.ScheduledNotifications}-template`);
              }}
              size="medium"
              testID={`${ImportType.ScheduledNotifications}-template-link`}
              variant="text"
            >
              <Text category="body-small">
                {t($ => $.notificationBuilder.actions.downloadTemplate)}
              </Text>
            </Button>
          </Text>
        </Stack>
      </DialogTitle>
      <VSpacer size="5" />
      <DialogContent sx={{ padding: 0 }}>
        <FileUpload
          acceptedFiles=".csv"
          buttonText={t($ => $.common.actions.upload)}
          onChangeFile={handleFileChange}
        />
      </DialogContent>
      <VSpacer size="4" />
      <VSpacer size="4" />
      <DialogActions sx={{ padding: 0 }}>
        <Button
          color="primary"
          onClick={() => {
            setSelectedFile(undefined);
            onClose();
          }}
          testID="csv-upload-cancel-button"
          variant="text"
        >
          {t($ => $.common.actions.cancel)}
        </Button>
        <Button
          color="primary"
          disabled={!selectedFile}
          onClick={handleSave}
          testID="csv-upload-save-button"
          variant="contained"
        >
          {t($ => $.common.actions.save)}
        </Button>
      </DialogActions>
      <VSpacer size="5" />
    </Dialog>
  );
};
